"use client";

import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/next";
import posthog from "posthog-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  type AnalyticsConsent,
  buildAnalyticsConsentCookieValue,
  readAnalyticsConsentFromCookieHeader,
} from "@/lib/analytics/consent";
import { resolveAnalyticsRuntimeConfig } from "@/lib/analytics/runtime-config";

type AnalyticsConsentContextValue = {
  consent: AnalyticsConsent | null;
  hasAnalyticsConsent: boolean;
  canTrackPostHogEvents: boolean;
  setConsent: (consent: AnalyticsConsent) => void;
};

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null);

let didInitPostHog = false;
const isDevelopment = process.env.NODE_ENV === "development";
const posthogProxyPath = process.env.NEXT_PUBLIC_POSTHOG_PROXY_PATH?.trim() || "/ph";
const runtimeConfig = resolveAnalyticsRuntimeConfig(process.env);

function resolvePostHogUiHost(): string | null {
  const explicitUiHost = process.env.NEXT_PUBLIC_POSTHOG_UI_HOST?.trim();
  if (explicitUiHost) return explicitUiHost;

  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim() ?? "";
  if (apiHost.includes("eu.i.posthog.com")) return "https://eu.posthog.com";
  if (apiHost.includes("us.i.posthog.com")) return "https://us.posthog.com";
  return null;
}

function readConsentFromDocumentCookie(): AnalyticsConsent | null {
  if (typeof document === "undefined") {
    return null;
  }

  return readAnalyticsConsentFromCookieHeader(document.cookie);
}

function persistConsentCookie(consent: AnalyticsConsent): void {
  if (typeof document === "undefined") {
    return;
  }

  const secure = typeof window !== "undefined" && window.location.protocol === "https:";
  document.cookie = buildAnalyticsConsentCookieValue(consent, { secure });
}

function initPostHog(): boolean {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!posthogKey) {
    if (isDevelopment) {
      console.warn(
        "[analytics] NEXT_PUBLIC_POSTHOG_KEY is missing. PostHog tracking is disabled."
      );
    }
    return false;
  }

  posthog.init(posthogKey, {
    api_host: posthogProxyPath,
    ui_host: resolvePostHogUiHost(),
    autocapture: true,
    capture_pageview: false,
    capture_pageleave: true,
    persistence: "localStorage",
    disable_session_recording: !runtimeConfig.enablePostHogSessionRecording,
    disable_surveys: !runtimeConfig.enablePostHogSurveys,
    disable_surveys_automatic_display: !runtimeConfig.enablePostHogSurveys,
    disable_external_dependency_loading: !runtimeConfig.enablePostHogExternalDependencies,
    advanced_disable_flags: runtimeConfig.disablePostHogRemoteFlags,
    capture_performance: false,
    loaded: (instance) => {
      if (isDevelopment) {
        instance.debug(true);
        console.info("[analytics] PostHog initialized (debug mode enabled).");
      }
    },
  });

  didInitPostHog = true;
  return true;
}

export function AnalyticsProvider({
  children,
  initialConsent,
}: {
  children: ReactNode;
  initialConsent: AnalyticsConsent | null;
}) {
  const [consent, setConsentState] = useState<AnalyticsConsent | null>(initialConsent);
  const [canTrackPostHogEvents, setCanTrackPostHogEvents] = useState<boolean>(false);

  useEffect(() => {
    const cookieConsent = readConsentFromDocumentCookie();
    if (cookieConsent && cookieConsent !== consent) {
      setConsentState(cookieConsent);
    }
  }, [consent]);

  const setConsent = useCallback((nextConsent: AnalyticsConsent) => {
    setConsentState(nextConsent);
    persistConsentCookie(nextConsent);
  }, []);

  const hasAnalyticsConsent = consent === "accepted";

  useEffect(() => {
    if (!hasAnalyticsConsent) {
      setCanTrackPostHogEvents(false);
      if (didInitPostHog) {
        posthog.opt_out_capturing();
      }
      return;
    }

    if (!didInitPostHog) {
      const initialized = initPostHog();
      setCanTrackPostHogEvents(initialized);
      return;
    }

    posthog.opt_in_capturing();
    setCanTrackPostHogEvents(true);
  }, [hasAnalyticsConsent]);

  const value = useMemo<AnalyticsConsentContextValue>(
    () => ({
      consent,
      hasAnalyticsConsent,
      canTrackPostHogEvents,
      setConsent,
    }),
    [consent, hasAnalyticsConsent, canTrackPostHogEvents, setConsent]
  );

  return (
    <AnalyticsConsentContext.Provider value={value}>
      {children}
      {hasAnalyticsConsent && runtimeConfig.enableVercelObservability ? (
        <>
          <VercelAnalytics />
          <SpeedInsights />
        </>
      ) : null}
    </AnalyticsConsentContext.Provider>
  );
}

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const value = useContext(AnalyticsConsentContext);
  if (!value) {
    throw new Error("useAnalyticsConsent must be used within AnalyticsProvider.");
  }

  return value;
}

export function captureAnalyticsEvent(event: string, properties?: Record<string, unknown>): void {
  if (!didInitPostHog) {
    return;
  }

  posthog.capture(event, properties);
}
